import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const MAX_RESCHEDULES_PER_CYCLE = 3;

interface RescheduleLimitsResult {
  canReschedule: boolean;
  remainingReschedules: number;
  currentCount: number;
  maxReschedules: number;
  loading: boolean;
  error: string | null;
  billingCycleStart: Date | null;
}

/**
 * Hook to check and enforce reschedule limits per billing cycle
 * Users are limited to 3 reschedules per billing cycle
 */
export const useRescheduleLimits = (userId: string | undefined): RescheduleLimitsResult => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCount, setCurrentCount] = useState(0);
  const [billingCycleStart, setBillingCycleStart] = useState<Date | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchRescheduleCounts = async () => {
      try {
        // Get billing cycle start from profile
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("billing_cycle_start")
          .eq("id", userId)
          .single();

        if (profileError) throw profileError;

        // Default to start of current month if no billing cycle set
        const cycleStart = profile?.billing_cycle_start
          ? new Date(profile.billing_cycle_start)
          : new Date(new Date().getFullYear(), new Date().getMonth(), 1);

        setBillingCycleStart(cycleStart);

        // Count reschedules since billing cycle start
        // Sum all reschedule_count values from user_pickups in current cycle
        const { data: pickups, error: pickupsError } = await supabase
          .from("user_pickups")
          .select("reschedule_count")
          .eq("user_id", userId)
          .gte("updated_at", cycleStart.toISOString());

        if (pickupsError) throw pickupsError;

        const totalReschedules = (pickups || []).reduce(
          (sum, p) => sum + (p.reschedule_count || 0),
          0
        );

        setCurrentCount(totalReschedules);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error("Error fetching reschedule limits:", err);
        }
        setError("Failed to check reschedule limits");
      } finally {
        setLoading(false);
      }
    };

    fetchRescheduleCounts();
  }, [userId]);

  const remainingReschedules = Math.max(0, MAX_RESCHEDULES_PER_CYCLE - currentCount);
  const canReschedule = remainingReschedules > 0;

  return {
    canReschedule,
    remainingReschedules,
    currentCount,
    maxReschedules: MAX_RESCHEDULES_PER_CYCLE,
    loading,
    error,
    billingCycleStart,
  };
};

/**
 * Increment the reschedule count for a pickup
 */
export const incrementRescheduleCount = async (pickupId: string): Promise<boolean> => {
  try {
    // First get current count
    const { data: pickup, error: fetchError } = await supabase
      .from("user_pickups")
      .select("reschedule_count")
      .eq("id", pickupId)
      .single();

    if (fetchError) throw fetchError;

    const currentCount = pickup?.reschedule_count || 0;

    // Update with incremented count
    const { error: updateError } = await supabase
      .from("user_pickups")
      .update({ reschedule_count: currentCount + 1 })
      .eq("id", pickupId);

    if (updateError) throw updateError;

    return true;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error("Error incrementing reschedule count:", err);
    }
    return false;
  }
};
